import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import {
  clearAppStoragePreservingAuth,
  hadCookieError,
  setHeaderTooLargeModalShown,
  wasHeaderTooLargeModalShown,
} from '../../utils/storageCleanup';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 12,
      maxWidth: 480,
      width: '100%',
      margin: 16,
    },
  },
  content: {
    padding: theme.spacing(2, 3),
  },
  iconBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
    color: theme.palette.warning.main,
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: '#1a1a2e',
  },
  message: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 1.6,
    marginBottom: 8,
  },
  cookieKb: {
    fontSize: 13,
    color: '#92400e',
    marginBottom: 0,
  },
  actions: {
    padding: theme.spacing(2, 3),
    gap: 12,
  },
  primaryButton: {
    textTransform: 'none',
    fontWeight: 600,
  },
  secondaryButton: {
    textTransform: 'none',
  },
}));

const EVENT_NAME = 'pingochat-header-too-large';

export function triggerCookieProblemModal() {
  if (typeof window === 'undefined') return;
  if (wasHeaderTooLargeModalShown()) return;
  setHeaderTooLargeModalShown();
  window.dispatchEvent(new CustomEvent(EVENT_NAME));
}

/** Tamanho em KB dos cookies visíveis (document.cookie). HttpOnly não entram. */
function getVisibleCookieSizeKB() {
  if (typeof document === 'undefined') return 0;
  const len = document.cookie ? document.cookie.length : 0;
  return (len / 1024).toFixed(2);
}

export default function CookieProblemModal() {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [cookieKB, setCookieKB] = useState('0.00');

  useEffect(() => {
    const handler = () => {
      setCookieKB(getVisibleCookieSizeKB());
      setOpen(true);
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []);

  // Ao entrar no sistema: se já ocorreu erro de cookie antes, mostrar o modal de novo
  useEffect(() => {
    if (hadCookieError() && !wasHeaderTooLargeModalShown()) {
      triggerCookieProblemModal();
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const refresh = () => setCookieKB(getVisibleCookieSizeKB());
    refresh();
    const t = setInterval(refresh, 2000);
    return () => clearInterval(t);
  }, [open]);

  const handleClearAndReload = () => {
    clearAppStoragePreservingAuth({ clearCaches: true, unregisterServiceWorkers: true });
    window.location.reload();
  };

  const handleClose = () => setOpen(false);

  return (
    <>
      <Dialog open={open} onClose={handleClose} className={classes.dialog}>
        <DialogTitle disableTypography className={classes.content}>
          <Box className={classes.iconBox}>
            <WarningIcon fontSize="medium" />
            <Typography className={classes.title}>Problema de cookies do navegador</Typography>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.content}>
          <Typography className={classes.message}>
            Seu navegador está enviando cookies grandes demais para a API e o servidor bloqueou a
            requisição. Clique no botão abaixo para limpar os dados do site e recarregar (seu login
            será mantido).
          </Typography>
          <Typography className={classes.cookieKb}>
            Cookies visíveis: ~{cookieKB} KB. Se o erro continuar, limpe os cookies da API nas
            configurações do navegador.
          </Typography>
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Button
            color="primary"
            variant="contained"
            onClick={handleClearAndReload}
            className={classes.primaryButton}
          >
            Limpar dados e recarregar
          </Button>
          <Button color="default" onClick={handleClose} className={classes.secondaryButton}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
